import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

import getMetrics from './getMetrics';
import { filterMetricsForTotals } from './filterMetricsForTotals';
import getLimitSetKeywordFilter from '../getLimitSetKeyworkFilter';

const getTermedHistogramResponses = async ({
  chartDef,
  filterInput,
  usedMetrics,
  isDashboardGadget,
  currentDateScope,
  toMetricInput,
  client,
  groupByField,
  isSecondaryRowsStacked,
}: {
  chartDef: V5ChartDefinition;
  filterInput: FilterInput;
  usedMetrics: Metrics.Metric[];
  isDashboardGadget?: boolean;
  currentDateScope: DateRangeInput;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  client: ApolloClient<NormalizedCacheObject>;
  groupByField: string;
  // Stacked secondary rows: also fetch each dimension A value's subtotals
  isSecondaryRowsStacked?: boolean;
}) => {
  const termedHistograms = await getMetrics({
    chartDef,
    filterInput,
    usedMetrics,
    isDashboardGadget,
    dateScope: currentDateScope,
    toMetricInput,
    client,
  });
  const limitedKeywordFilter = getLimitSetKeywordFilter(
    termedHistograms,
    groupByField,
    chartDef.dimensionA,
  );
  const totalsMetrics = filterMetricsForTotals({ usedMetrics, chartDef });
  const histogramForTerms =
    totalsMetrics.length > 0
      ? await getMetrics({
          chartDef,
          filterInput,
          getTotals: true,
          usedMetrics: totalsMetrics,
          isDashboardGadget,
          dateScope: currentDateScope,
          toMetricInput,
          client,
          limitedKeywordFilter,
        })
      : [];

  // Stacked secondary rows: each dimension A value's own trend (the rows
  // query without the secondary dimension, for the same A values), so the
  // server aggregates every subtotal: right for averages and compound
  // metrics, where adding up the secondary rows wouldn't be
  if (isSecondaryRowsStacked && isSecondaryDimensionAInEffect(chartDef)) {
    const subtotals = await getMetrics({
      chartDef: { ...chartDef, secondaryDimensionA: undefined },
      filterInput,
      usedMetrics,
      isDashboardGadget,
      dateScope: currentDateScope,
      toMetricInput,
      client,
      limitedKeywordFilter,
    });
    return [termedHistograms, histogramForTerms, [], [], subtotals];
  }

  return [termedHistograms, histogramForTerms];
};

export default getTermedHistogramResponses;
